// src/components/common/EstimateSearchBar.tsx
//
// 견적 검색 카드 — 흰 서피스 + 1px #E4E7EA 보더 + radius 12.
// 기본 검색 필드 그룹, 상세 검색 토글, 하단 액션 행으로 구성된다.
// 공개 인터페이스와 동작은 그대로이고 시각 표면만 새 디자인 시스템을 따른다.

import { useState } from "react";
import {
  Box,
  Button,
  Collapse,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

import {
  EstimateSearchCriteria,
  defaultCriteria,
} from "../../contracts/estimates/searchTypes.ts";
import {
  COMPANY_IN_CHARGE_TYPE,
  COMPARISION_COMPANY_IN_CHARGE_TYPE,
  SALE_TYPE,
} from "../../contracts/estimates/types.ts";
import { designTokens } from "../../theme.ts";

interface EstimateSearchBarProps {
  initialCriteria?: EstimateSearchCriteria;
  onSearch: (criteria: EstimateSearchCriteria) => void;
  onReset?: () => void;
  /** 다이얼로그 등에서 contentType을 강제하고 싶을 때 (해당 필드 disabled) */
  lockedContentType?: typeof SALE_TYPE[number];
}

const C = {
  ink: "#171A1E",
  sub: "#6B7178",
  border: "#E4E7EA",
  surface: "#FFFFFF",
  field: "#F9FAFB",
  stop: "#B12B2B",
};

const COMPANY_IN_CHARGE_OPTIONS: string[] = [
  ...COMPANY_IN_CHARGE_TYPE,
  ...COMPARISION_COMPANY_IN_CHARGE_TYPE,
];

// 카드 안의 모든 TextField 를 같은 리듬으로 맞춘다. 보더/포커스 색은 테마가
// 칠하고 여기서는 라벨 타이포와 인풋 배경만 고정한다.
const fieldSx = {
  "& .MuiOutlinedInput-root": {
    bgcolor: C.field,
    fontSize: 13.5,
  },
  "& .MuiInputLabel-root": {
    fontFamily: designTokens.fonts.sans,
    fontSize: 12.5,
    fontWeight: 500,
  },
  "& .MuiFormHelperText-root": {
    fontFamily: designTokens.fonts.sans,
    fontSize: 12,
    marginLeft: 0,
    marginTop: 0.5,
  },
};

// 필드 그룹 위에 붙는 작은 한글 캡션.
function GroupTag({ label }: { code?: string; label: string }) {
  return (
    <Typography
      component="div"
      sx={{
        mb: 0.75,
        fontFamily: designTokens.fonts.sans,
        fontSize: 12.5,
        fontWeight: 600,
        color: C.sub,
      }}
    >
      {label}
    </Typography>
  );
}

export default function EstimateSearchBar({
  initialCriteria,
  onSearch,
  onReset,
  lockedContentType,
}: EstimateSearchBarProps) {
  const [criteria, setCriteria] = useState<EstimateSearchCriteria>(() => ({
    ...defaultCriteria,
    ...initialCriteria,
    ...(lockedContentType ? { contentType: lockedContentType } : {}),
  }));
  const [showDetail, setShowDetail] = useState(false);

  const dateInvalid =
    !!criteria.dateFrom &&
    !!criteria.dateTo &&
    criteria.dateFrom > criteria.dateTo;

  const update = <K extends keyof EstimateSearchCriteria>(
    key: K,
    value: EstimateSearchCriteria[K]
  ) => {
    setCriteria((prev) => ({ ...prev, [key]: value }));
  };

  const handleSearch = () => {
    if (dateInvalid) return;
    // Strip empty strings so backend treats them as "no filter".
    const cleaned: EstimateSearchCriteria = {};
    (Object.keys(criteria) as Array<keyof EstimateSearchCriteria>).forEach(
      (k) => {
        const v = criteria[k];
        if (v !== undefined && v !== "") (cleaned as any)[k] = v;
      }
    );
    onSearch(cleaned);
  };

  const handleReset = () => {
    const reset: EstimateSearchCriteria = {
      ...defaultCriteria,
      ...(lockedContentType ? { contentType: lockedContentType } : {}),
    };
    setCriteria(reset);
    setShowDetail(false);
    if (onReset) onReset();
    else onSearch(reset);
  };

  return (
    <Box
      sx={{
        bgcolor: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: "12px",
        boxShadow: "0 1px 2px rgba(16,24,32,0.06)",
        p: { xs: 2, md: 2.5 },
        mb: 2.5,
      }}
    >
      {/* ─── Basic search: 5 fields, visually grouped ─────────────────── */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          gap: { xs: 2, lg: 2.5 },
          alignItems: { lg: "flex-end" },
        }}
      >
        {/* DEALER · 공급/구분 group */}
        <Box sx={{ flex: { lg: "0 0 auto" }, minWidth: 0 }}>
          <GroupTag label="공급" />
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.25}
            sx={{ flexWrap: "wrap" }}
          >
            <TextField
              select
              size="small"
              label="공급업체"
              value={criteria.companyInCharge ?? ""}
              onChange={(e) => update("companyInCharge", e.target.value)}
              sx={{ ...fieldSx, minWidth: 140 }}
            >
              <MenuItem value="">전체</MenuItem>
              {COMPANY_IN_CHARGE_OPTIONS.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label="구분"
              value={criteria.contentType ?? ""}
              onChange={(e) => update("contentType", e.target.value)}
              disabled={!!lockedContentType}
              sx={{ ...fieldSx, minWidth: 110 }}
            >
              <MenuItem value="">전체</MenuItem>
              {SALE_TYPE.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </Box>

        {/* PARTY · 거래처/수신자 group */}
        <Box sx={{ flex: { lg: 1 }, minWidth: 0 }}>
          <GroupTag label="거래처" />
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.25}
            sx={{ flexWrap: "wrap" }}
          >
            <TextField
              size="small"
              label="업체명"
              value={criteria.companyName ?? ""}
              onChange={(e) => update("companyName", e.target.value)}
              sx={{ ...fieldSx, flex: 1, minWidth: 160 }}
            />
            <TextField
              size="small"
              label="수신자"
              value={criteria.recipientName ?? ""}
              onChange={(e) => update("recipientName", e.target.value)}
              sx={{ ...fieldSx, flex: 1, minWidth: 140 }}
            />
          </Stack>
        </Box>

        {/* RANGE · 기간 group */}
        <Box sx={{ flex: { lg: "0 0 auto" }, minWidth: 0 }}>
          <GroupTag label="기간" />
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.25}
            sx={{ flexWrap: "wrap" }}
          >
            <TextField
              select
              size="small"
              label="날짜 종류"
              value={criteria.dateField ?? "estimateDate"}
              onChange={(e) =>
                update(
                  "dateField",
                  e.target.value as EstimateSearchCriteria["dateField"]
                )
              }
              sx={{ ...fieldSx, minWidth: 130 }}
            >
              <MenuItem value="estimateDate">견적일</MenuItem>
              <MenuItem value="createdTimestamp">작성일</MenuItem>
            </TextField>

            <TextField
              size="small"
              type="date"
              label="시작일"
              InputLabelProps={{ shrink: true }}
              value={criteria.dateFrom ?? ""}
              onChange={(e) => update("dateFrom", e.target.value)}
              error={dateInvalid}
              sx={{ ...fieldSx, minWidth: 150 }}
            />

            <TextField
              size="small"
              type="date"
              label="종료일"
              InputLabelProps={{ shrink: true }}
              value={criteria.dateTo ?? ""}
              onChange={(e) => update("dateTo", e.target.value)}
              error={dateInvalid}
              helperText={dateInvalid ? "종료일이 시작일보다 빠릅니다" : undefined}
              sx={{ ...fieldSx, minWidth: 150 }}
            />
          </Stack>
        </Box>
      </Box>

      {/* ─── 상세 검색 토글 ──────────────────────────────────────────── */}
      <Box
        sx={{
          mt: 2,
          mb: showDetail ? 1.5 : 0,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Button
          onClick={() => setShowDetail((s) => !s)}
          variant="text"
          size="small"
          aria-expanded={showDetail}
          aria-controls="estimate-detail-panel"
          startIcon={
            showDetail ? (
              <ExpandLessIcon sx={{ fontSize: 18 }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: 18 }} />
            )
          }
          sx={{
            px: 1,
            fontFamily: designTokens.fonts.sans,
            fontSize: 13,
            fontWeight: 600,
            color: C.sub,
            "&:hover": { bgcolor: "#F2F4F5", color: C.ink },
          }}
        >
          상세 검색
        </Button>
        <Box
          aria-hidden
          sx={{ flex: 1, height: 0, borderTop: `1px solid ${C.border}` }}
        />
      </Box>

      <Collapse in={showDetail} id="estimate-detail-panel">
        <Box
          sx={{
            border: `1px solid ${C.border}`,
            borderRadius: "12px",
            bgcolor: C.field,
            p: { xs: 1.75, md: 2 },
          }}
        >
          {/* DOC · 견적서 식별 */}
          <GroupTag label="견적서" />
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.25}
            sx={{ flexWrap: "wrap", mb: 1.5 }}
          >
            <TextField
              size="small"
              label="견적서 제목"
              value={criteria.contentTitle ?? ""}
              onChange={(e) => update("contentTitle", e.target.value)}
              sx={{ ...fieldSx, flex: 1, minWidth: 200 }}
            />
            <TextField
              size="small"
              label="현장명"
              value={criteria.siteName ?? ""}
              onChange={(e) => update("siteName", e.target.value)}
              sx={{ ...fieldSx, flex: 1, minWidth: 160 }}
            />
            <TextField
              size="small"
              label="발신자"
              value={criteria.sender ?? ""}
              onChange={(e) => update("sender", e.target.value)}
              sx={{ ...fieldSx, flex: 1, minWidth: 140 }}
            />
          </Stack>

          {/* SHIP · 납품/담당 */}
          <GroupTag label="납품" />
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.25}
            sx={{ flexWrap: "wrap", mb: 1.5 }}
          >
            <TextField
              size="small"
              label="납품 장소"
              value={criteria.deliveryLocation ?? ""}
              onChange={(e) => update("deliveryLocation", e.target.value)}
              sx={{ ...fieldSx, flex: 1, minWidth: 200 }}
            />
            <TextField
              size="small"
              label="담당자"
              value={criteria.personInCharge ?? ""}
              onChange={(e) => update("personInCharge", e.target.value)}
              sx={{ ...fieldSx, flex: 1, minWidth: 140 }}
            />
          </Stack>

          {/* ITEM · 품목 */}
          <GroupTag label="품목" />
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.25}
            sx={{ flexWrap: "wrap" }}
          >
            <TextField
              size="small"
              label="품목명"
              value={criteria.itemName ?? ""}
              onChange={(e) => update("itemName", e.target.value)}
              sx={{ ...fieldSx, flex: 1, minWidth: 200 }}
            />
            <TextField
              size="small"
              label="품목 규격"
              value={criteria.itemSize ?? ""}
              onChange={(e) => update("itemSize", e.target.value)}
              sx={{ ...fieldSx, flex: 1, minWidth: 200 }}
            />
          </Stack>
        </Box>
      </Collapse>

      {/* ─── 액션 행 ─────────────────────────────────────────────────── */}
      <Box
        sx={{
          mt: 2.25,
          pt: 1.75,
          borderTop: `1px solid ${C.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Typography
          component="div"
          sx={{
            fontFamily: designTokens.fonts.sans,
            fontSize: 12.5,
            fontWeight: dateInvalid ? 600 : 400,
            color: dateInvalid ? C.stop : C.sub,
          }}
        >
          {dateInvalid ? "기간 범위가 올바르지 않습니다" : "조건을 선택한 뒤 검색하세요"}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            onClick={handleReset}
            variant="outlined"
            size="small"
            startIcon={<RestartAltIcon sx={{ fontSize: 16 }} />}
          >
            초기화
          </Button>
          <Button
            onClick={handleSearch}
            variant="contained"
            color="primary"
            size="small"
            disabled={dateInvalid}
            startIcon={<SearchIcon sx={{ fontSize: 16 }} />}
          >
            검색
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}
